<template>
  <div class="flexible-checkin">
    <!-- TITULO -->
    <b-row class="form--title">
      <b-col class="form--title__spacing">
        <PmwHeading
          size="paragraph-80"
          weight="weight-light"
          class="form--title__text"
        >
          Check-in Flexível
        </PmwHeading>

        <b-row class="form--row">
          <b-col>
            <!-- INPUT DE QUANTIDADE DE DIAS DO CHECKIN -->
            <PmwFormInput
              v-model.number="fluctuationData.diasToleranciaCheckin"
              :infoIcon="true"
              :error="hasCheckInError"
              :showErrorFeedbackMessage="hasCheckInError"
              :disabled="hasAccommodationPeriod"
              feedbackMessage="O valor deve ser maior que 0"
              tooltipMinWidth="350"
              label="Quantidade de dias"
              infoText="
              Defina a quantidade de dias que poderá flexibilizar a ativação desta tarifa precedente ao inicio da hospedagem. Quando o check-in da busca realizada pelo usuário estiver dentro deste período, a tarifa será ativada sob condições especiais.
              "
              placeholder="0"
              size="small"
              type="number"
              sufix="dias"
              class="form--input"
            />
          </b-col>

          <b-col>
            <!-- INPUT DO COEFICIENTE DO CHECKIN -->
            <PmwFormInput
              v-model.number="fluctuationData.coeficienteCheckin"
              :required="fluctuationData.diasToleranciaCheckin ? true : false"
              :disabled="hasAccommodationPeriod"
              :error="errors.includes('CoeficienteCheckin')"
              data-error="CoeficienteCheckin"
              label="Coeficiente"
              placeholder="0"
              size="small"
              type="number"
              sufix="%"
              class="form--input"
              @blur="coeficienteHandler(fluctuationData.coeficienteCheckin)"
            />
          </b-col>
        </b-row>
      </b-col>

      <b-col>
        <PmwHeading
          size="paragraph-80"
          weight="weight-light"
          class="form--title__text"
        >
          Check-out Flexível
        </PmwHeading>

        <b-row class="form--row">
          <b-col>
            <!-- INPUT DA QUANTIDADE DE DIAS DO CHECKOUT -->
            <PmwFormInput
              v-model.number="fluctuationData.diasToleranciaCheckout"
              :infoIcon="true"
              :error="hasCheckOutError"
              :showErrorFeedbackMessage="hasCheckOutError"
              :disabled="hasAccommodationPeriod"
              feedbackMessage="O valor deve ser maior que 0"
              tooltipMinWidth="350"
              label="Quantidade de dias"
              infoText="
              Defina a quantidade de dias que poderá flexibilizar a ativação desta tarifa após o périodo da hospedagem. Quando o check-out da busca realizada pelo usuário estiver dentro deste período, a tarifa será ativada sob condições especiais.
              "
              placeholder="0"
              size="small"
              type="number"
              sufix="dias"
              class="form--input"
            />
          </b-col>

          <b-col>
            <!-- INPUT DO COEFICIENTE DO CHECKOUT -->
            <PmwFormInput
              v-model.number="fluctuationData.coeficienteCheckout"
              :required="fluctuationData.diasToleranciaCheckout ? true : false"
              :disabled="hasAccommodationPeriod"
              :error="errors.includes('CoeficienteCheckout')"
              data-error="CoeficienteCheckout"
              label="Coeficiente"
              placeholder="0"
              size="small"
              type="number"
              sufix="%"
              class="form--input"
              @blur="coeficienteHandler(fluctuationData.coeficienteCheckout)"
            />
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <!-- MODAL DE ALERTA DE VALOR -->
    <InputModalAlert
      :show="showCoefficientAlert"
      firstLine="O valor preenchido foi menor que 50%."
      mainText="Lembre-se que o coeficiente é a porcentagem referente ao valor do calendário, e não a porcentagem de desconto."
      lastLine="Tem certeza que quer continuar?"
      @closeAlert="showCoefficientAlert = false"
    />
  </div>
</template>

<script>
import PmwHeading from '@solucoes/components-lib-tmp/src/components/elements/PmwHeading'
import PmwFormInput from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormInput'
import InputModalAlert from '../InputModalAlert.vue'
import debounce from 'lodash.debounce'

export default {
  name: 'FlexibleCheckIn',
  components: {
    PmwHeading,
    PmwFormInput,
    InputModalAlert
  },

  props: {
    fluctuationData: {
      type: Object,
      default: () => ({})
    },

    /** Quantidade de dias do checkin */
    checkinQuantity: {
      type: Number,
      default: null
    },

    /** Erros (array de erros que foram identificados pelo back-end) */
    errors: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      showCoefficientAlert: false
    }
  },

  computed: {
    hasCheckInError() {
      if (
        this.fluctuationData.diasToleranciaCheckin === 0 &&
        this.fluctuationData.periodosHospedagem?.length
      ) {
        return true
      }

      return false
    },

    hasCheckOutError() {
      if (
        this.fluctuationData.diasToleranciaCheckout === 0 &&
        this.fluctuationData.periodosHospedagem?.length
      ) {
        return true
      }

      return false
    },

    hasAccommodationPeriod() {
      if (
        this.fluctuationData.periodosHospedagem?.length <= 0 ||
        !this.fluctuationData.periodosHospedagem
      ) {
        return true
      }

      return false
    }
  },

  methods: {
    /** Validar valor do coeficiente
     * @param {Number} value - Valor do coeficiente a ser validado
     */
    coeficienteHandler: debounce(function(value) {
      if (value <= 50) {
        this.showCoefficientAlert = true
      }
    }, 500)
  }
}
</script>

<style lang="scss" scoped>
.flexible-checkin {
  min-width: auto;
  .form {
    &--input {
      /** Alterar cores novas do select */
      ::v-deep {
        .pmw-form-field--label {
          font-weight: font-weight('weight-medium');
        }
        .form-field--label-wrapper {
          line-height: 21px;
          margin-bottom: 3px;
        }
        .pmw-input {
          background: $white;
        }
        .pmw-input--clear {
          z-index: 0;
        }
        .pmw-input__focus {
          border-color: $pmw-blue;
        }

        .pmw-input--field {
          background: $pmw-white;

          &:focus {
            border-color: $pmw-blue;
          }
        }

        .pmw-input__disabled {
          input {
            background: pmw-colors('grey-20');
            color: $pmw-gray-darker;
          }
        }

        .pmw-custom-tooltip {
          line-height: 18px;
        }
      }
    }

    &--row {
      display: flex;
      align-items: flex-end;
      flex-wrap: wrap;
      gap: 28px;
    }

    &--title {
      margin-bottom: 24px;
      min-width: auto;
      display: inline-flex;

      &__text {
        margin-bottom: 24px;
      }

      &__spacing {
        margin-right: 80px;
      }
    }
  }
}
</style>
